/**
 * OzPol Transparent — Donor Profile View
 * Shows full donation history and party split for a single donor.
 */
import { ExternalLink } from "lucide-react";
import { fmt } from "../../utils/donationsUtils";

export default function DonorProfileView({ profile, isDark, onBack }) {
  const surface = isDark ? "#1E293B" : "#FFFFFF";
  const text    = isDark ? "#F8FAFC" : "#0F172A";
  const muted   = isDark ? "#94A3B8" : "#6B7280";
  const bdr     = isDark ? "#334155" : "#000000";
  const donor   = profile.donor;
  const maxAmt  = Math.max(...profile.total_by_party.map(p => p.total_aud), 1);

  return (
    <div data-testid="donor-profile-view">
      <button
        onClick={onBack}
        style={{
          display: "flex", alignItems: "center", gap: 6, marginBottom: 16,
          fontSize: 12, fontWeight: 700, color: muted, background: "none",
          border: "none", cursor: "pointer", padding: 0,
        }}>
        ← Back to donors
      </button>

      {/* Header */}
      <div style={{
        border: "2px solid #000", padding: 20, marginBottom: 16,
        background: surface, boxShadow: "4px 4px 0 #000",
        borderLeft: "6px solid #FFC82E",
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 900, fontFamily: "Outfit, sans-serif", color: text, marginBottom: 3 }}>
              {donor.name}
            </div>
            <div style={{ fontSize: 12, color: muted, marginBottom: 8 }}>{donor.full_name}</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 8px", border: "2px solid #000", background: "#0F172A", color: "#fff" }}>
                {donor.category}
              </span>
              <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 8px", border: "2px solid #000", color: text }}>
                {donor.industry}
              </span>
              {donor.parent_entity && (
                <span style={{ fontSize: 9, color: muted, padding: "2px 8px", border: `1px solid ${bdr}` }}>
                  Part of {donor.parent_entity}
                </span>
              )}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 26, fontWeight: 900, fontFamily: "Outfit, sans-serif", color: "#FFC82E" }}>
              {fmt(profile.total_all_aud)}
            </div>
            <div style={{ fontSize: 10, color: muted }}>Total Declared (both years)</div>
            {profile.cross_party && (
              <div style={{
                marginTop: 6, fontSize: 9, fontWeight: 800,
                padding: "3px 8px", border: "2px solid #EF4444",
                color: "#EF4444", background: "#FEE2E2", display: "inline-block",
              }}>
                CROSS-PARTY DONOR
              </div>
            )}
          </div>
        </div>
        <p style={{ fontSize: 13, color: text, lineHeight: 1.7, margin: "14px 0 10px" }}>{donor.description}</p>
        {/* Action links */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {donor.website && (
            <a
              href={donor.website}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="donor-profile-website-link"
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "7px 14px", fontSize: 11, fontWeight: 800,
                border: "2px solid #000", background: "#FFC82E", color: "#000",
                textDecoration: "none", boxShadow: "2px 2px 0 #000",
                fontFamily: "Outfit, sans-serif",
              }}>
              <ExternalLink style={{ width: 12, height: 12 }} />
              Official Website
            </a>
          )}
          <a
            href={donor.aec_search_url || "https://transparency.aec.gov.au/"}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="donor-profile-aec-link"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "7px 14px", fontSize: 11, fontWeight: 800,
              border: "2px solid #000", background: "#0F172A", color: "#fff",
              textDecoration: "none", boxShadow: "2px 2px 0 #000",
              fontFamily: "Outfit, sans-serif",
            }}>
            <ExternalLink style={{ width: 12, height: 12 }} />
            AEC Transparency Register
          </a>
        </div>
      </div>

      {/* Party split */}
      {profile.total_by_party.length > 0 && (
        <div style={{ border: "2px solid #000", padding: 16, marginBottom: 16, background: surface, boxShadow: "3px 3px 0 #000" }}>
          <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.07em", color: muted, marginBottom: 12 }}>
            FUNDING SPLIT BY PARTY (ALL YEARS COMBINED)
          </div>
          {profile.total_by_party.map(p => (
            <div key={p.party_id} style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, flexWrap: "wrap", gap: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 12, height: 12, background: p.color, border: "1px solid #000" }} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: text }}>{p.party_name}</span>
                  <span style={{ fontSize: 9, fontWeight: 700, color: muted }}>({p.abbreviation})</span>
                </div>
                <div style={{ display: "flex", gap: 12 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: text }}>{fmt(p.total_aud)}</span>
                  <span style={{ fontSize: 13, fontWeight: 800, color: p.color }}>{p.pct}%</span>
                </div>
              </div>
              <div style={{ background: isDark ? "#0F172A" : "#E5E7EB", height: 12, border: "1px solid #000" }}>
                <div style={{
                  width: `${(p.total_aud / maxAmt) * 100}%`, height: "100%",
                  background: p.color, transition: "width 0.5s ease",
                }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Donation history */}
      <div style={{ border: "2px solid #000", overflow: "hidden", boxShadow: "3px 3px 0 #000" }}>
        <div style={{ padding: "10px 16px", background: "#0F172A", color: "#fff" }}>
          <span style={{ fontWeight: 800, fontSize: 13 }}>DONATION HISTORY — {donor.name.toUpperCase()}</span>
        </div>
        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 380 }}>
            <thead>
              <tr style={{ background: isDark ? "#1E293B" : "#F3F4F6" }}>
                {["Financial Year", "Party", "Amount", "Type"].map(h => (
                  <th key={h} style={{
                    padding: "10px 12px", fontSize: 9, fontWeight: 800,
                    letterSpacing: "0.07em", textAlign: "left",
                    borderBottom: "2px solid #000", color: text, whiteSpace: "nowrap",
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {profile.by_year.map((r, i) => (
                <tr key={i} style={{
                  background: i % 2 === 0 ? surface : (isDark ? "#0F172A" : "#F9F7F2"),
                  borderBottom: `1px solid ${isDark ? "#334155" : "#E5E7EB"}`,
                }}>
                  <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: text, whiteSpace: "nowrap" }}>{r.financial_year}</td>
                  <td style={{ padding: "10px 12px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ width: 10, height: 10, background: r.color, border: "1px solid #000", flexShrink: 0 }} />
                      <span style={{ fontSize: 12, color: text, whiteSpace: "nowrap" }}>{r.party_name}</span>
                    </div>
                  </td>
                  <td style={{ padding: "10px 12px", fontSize: 14, fontWeight: 800, fontFamily: "Outfit, sans-serif", color: text, whiteSpace: "nowrap" }}>
                    {fmt(r.amount_aud)}
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    <span style={{ fontSize: 9, fontWeight: 700, color: muted, padding: "2px 6px", border: `1px solid ${bdr}`, whiteSpace: "nowrap" }}>
                      {r.type}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
